import { useSyncExternalStore } from "react";

export type ThemePreference = "dark" | "light" | "system";
export type Density = "comfortable" | "compact";
export type Landing = "tester" | "admin" | "runs";

export interface Preferences {
  theme: ThemePreference;
  density: Density;
  landing: Landing;
  timeDisplay: "relative" | "absolute";
  timeZone: "local" | "utc";
  logWrap: boolean;
  notifyOnFinish: boolean;
  sidebarCollapsed: boolean;
}

export const DEFAULT_PREFERENCES: Preferences = {
  theme: "dark",
  density: "comfortable",
  landing: "tester",
  timeDisplay: "relative",
  timeZone: "local",
  logWrap: false,
  notifyOnFinish: false,
  sidebarCollapsed: false,
};

const ALLOWED: { [K in keyof Preferences]: (v: unknown) => v is Preferences[K] } = {
  theme: (v): v is ThemePreference => v === "dark" || v === "light" || v === "system",
  density: (v): v is Density => v === "comfortable" || v === "compact",
  landing: (v): v is Landing => v === "tester" || v === "admin" || v === "runs",
  timeDisplay: (v): v is "relative" | "absolute" => v === "relative" || v === "absolute",
  timeZone: (v): v is "local" | "utc" => v === "local" || v === "utc",
  logWrap: (v): v is boolean => typeof v === "boolean",
  notifyOnFinish: (v): v is boolean => typeof v === "boolean",
  sidebarCollapsed: (v): v is boolean => typeof v === "boolean",
};

const PREFIX = "ats.pref.";
const listeners = new Set<() => void>();
let cache: Preferences | null = null;

function readKey<K extends keyof Preferences>(key: K): Preferences[K] {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw === null) return DEFAULT_PREFERENCES[key];
    const parsed: unknown = JSON.parse(raw);
    return ALLOWED[key](parsed) ? parsed : DEFAULT_PREFERENCES[key];
  } catch {
    return DEFAULT_PREFERENCES[key];
  }
}

export function loadPreferences(): Preferences {
  const out = { ...DEFAULT_PREFERENCES };
  for (const key of Object.keys(DEFAULT_PREFERENCES) as Array<keyof Preferences>) {
    (out as Record<string, unknown>)[key] = readKey(key);
  }
  return out;
}

export function savePreferences(p: Preferences): void {
  for (const key of Object.keys(p) as Array<keyof Preferences>) {
    try {
      localStorage.setItem(PREFIX + key, JSON.stringify(p[key]));
    } catch {
      return;
    }
  }
}

function snapshot(): Preferences {
  cache ??= loadPreferences();
  return cache;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(PREFIX)) {
      cache = null;
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function updatePreferences(patch: Partial<Preferences>) {
  const next = { ...snapshot(), ...patch };
  cache = next;
  savePreferences(next);
  for (const l of listeners) l();
}

export function resetPreferencesCache() {
  cache = null;
}

export function usePreferences(): [Preferences, (patch: Partial<Preferences>) => void] {
  const prefs = useSyncExternalStore(subscribe, snapshot, () => DEFAULT_PREFERENCES);
  return [prefs, updatePreferences];
}
